import { useMemo } from 'react';

import { useCompany } from 'context/company';
import { useUser } from 'context/user';
import useInvoices from 'hooks/useInvoices';
import { useTeamsSWR } from 'hooks/useTeamsSWR';
import { getTeamIDsToFilter } from 'utils/app';
import { buildInvoiceAvailabilityDaysResolver, getCurrentTeamInvoices, getOpenInvoiceCounts } from 'utils/invoice';

/**
 * Open-invoice counts for whatever the sidebar's team menu currently selects.
 *
 * Every inner page and the sidebar badge read this one hook, so the same invoice cannot be counted
 * two ways across two surfaces. The home dashboard is deliberately company-wide and reads the counts
 * off useInvoices instead.
 */
export default function useTeamScopedInvoiceCounts({ isAdminPoV }: { isAdminPoV?: boolean } = {}) {
  const { company } = useCompany();
  const { teams } = useTeamsSWR();
  const { showAllTeamsContracts, currentTeamId } = useUser() || {};

  const {
    allOpenInvoices,
    unapproved: { isListValidating: isUnapprovedValidating, isListPending: isUnapprovedPending },
    approved: { isListValidating: isApprovedValidating, isListPending: isApprovedPending },
  } = useInvoices({ companyID: company?.companyID, isAdminPoV });

  const isValidating = isUnapprovedValidating || isApprovedValidating;
  // First load only. Skeletons gate on this: isValidating is true on every background refresh, so
  // gating on it swapped populated cards back to placeholders each time the window regained focus.
  const isPending = isUnapprovedPending || isApprovedPending;

  const teamIdsToFilter = useMemo(
    () => getTeamIDsToFilter({ showAllTeamsContracts, teams, currentTeamId }),
    [showAllTeamsContracts, teams, currentTeamId],
  );

  // SWR serves the cached list while it revalidates, so counting is gated on the data being there
  // rather than on isValidating. Gating on isValidating dropped every count to zero on each
  // background refresh, which is how the sidebar badge and the page it links to came to disagree.
  const invoices = useMemo(() => {
    if (!teams || !allOpenInvoices?.length) return [];
    return getCurrentTeamInvoices({ invoices: allOpenInvoices, teams, teamIdsToFilter });
  }, [teams, allOpenInvoices, teamIdsToFilter]);

  const getDaysForInvoice = useMemo(
    () => buildInvoiceAvailabilityDaysResolver({ teams, teamsInfo: company?.teamsById }),
    [teams, company?.teamsById],
  );

  const counts = useMemo(
    () => getOpenInvoiceCounts(invoices, company?.settings?.paymentAllowedDaysBeforeDueDate, getDaysForInvoice),
    [invoices, company?.settings?.paymentAllowedDaysBeforeDueDate, getDaysForInvoice],
  );

  return { ...counts, invoices, teamIdsToFilter, getDaysForInvoice, isPending, isValidating };
}
